import { useEffect, useRef } from 'react';

/**
 * Centralised dismissable-popup behaviour for menus / dropdowns.
 *
 * Phase 2.3 introduced this hook so the WBS toolbar burger (and any
 * future burger / popover) can opt into the same UX contract:
 *   - clicking outside the container closes the menu
 *   - pressing Escape closes the menu
 *   - while open, focus is trapped inside the container — Tab from the
 *     last item wraps to the first, Shift+Tab from the first wraps to
 *     the last, and the first focusable element receives focus on open
 *
 * The hook is deliberately framework-agnostic about the *trigger*. The
 * caller is responsible for rendering the toggle button and the menu
 * panel; it just hands us the panel ref + an `onClose` callback. We do
 * *not* call `onClose` for clicks on the toggle button itself — only
 * truly outside clicks — by skipping pointer events when the toggle
 * ref's element contains the event target.
 *
 * SSR-safe: every effect short-circuits when `window` is undefined.
 */
export interface UseDismissableOptions {
  /** Whether the panel is currently open. Effects no-op when false. */
  open: boolean;
  /** Called for outside-click and Escape keypress. */
  onClose: () => void;
  /** Ref to the panel container — clicks inside it are ignored. */
  containerRef: React.RefObject<HTMLElement | null>;
  /**
   * Optional ref to the trigger button. Clicks on this element are
   * ignored so the existing toggle handler can do its own work without
   * the outside-click listener firing first and clobbering it.
   */
  triggerRef?: React.RefObject<HTMLElement | null>;
  /**
   * When true, focus is trapped inside the panel while open. Defaults
   * to true — burger menus and dropdowns generally want this. Disable
   * for hover-style popovers that shouldn't steal focus.
   */
  trapFocus?: boolean;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Wires outside-click + Esc dismissal and (optionally) focus-trap to a
 * dropdown / menu panel. Returns nothing — the caller owns the open
 * state and triggers re-renders by toggling it.
 */
export function useDismissable({
  open,
  onClose,
  containerRef,
  triggerRef,
  trapFocus = true,
}: UseDismissableOptions): void {
  // Stable callback ref so the outside-click / keydown listeners don't
  // need to re-bind every time the parent re-renders.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Outside-click + Escape dismissal.
  useEffect(() => {
    if (!open) return;
    if (typeof window === 'undefined') return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const panel = containerRef.current;
      if (!panel) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (panel.contains(target)) return;
      const trigger = triggerRef?.current;
      if (trigger && trigger.contains(target)) return;
      onCloseRef.current();
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, containerRef, triggerRef]);

  // Focus trap. Run after mount so the panel is in the DOM by the time
  // we look up focusable elements.
  useEffect(() => {
    if (!open) return;
    if (!trapFocus) return;
    if (typeof window === 'undefined') return;
    const panel = containerRef.current;
    if (!panel) return;

    // Capture the element that had focus before we opened so we can
    // hand focus back when the panel closes (consumer-driven via the
    // dependency on `open`).
    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const focusables = (): HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => !el.hasAttribute('data-focus-trap-skip'),
      );

    // Move focus inside on open. Defer to a microtask so React has
    // committed any focusable children rendered in the same flush.
    const items = focusables();
    if (items.length > 0) {
      items[0]!.focus();
    } else {
      // No focusable children — focus the panel itself if it can take
      // focus (we set tabindex="-1" on roving menus).
      if (typeof panel.focus === 'function') panel.focus();
    }

    const handleKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) {
        event.preventDefault();
        return;
      }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      // When focus has somehow escaped the panel, snap it back to the
      // first item rather than letting Tab leak into background content.
      if (!active || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }
      if (event.shiftKey) {
        if (active === first) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      // Restore the previously-focused element so opening / closing the
      // menu doesn't leave focus stranded inside an unmounted node.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open, trapFocus, containerRef]);
}
